import { Injectable } from '@angular/core';
import { LocalAuth } from 'src/app/utils/interfaces/new/localauth.interface';
import { Profil } from 'src/app/utils/interfaces/new/profil.interface';
import { Account } from 'src/app/utils/interfaces/old/account-interface';
import { CabinetService } from '../office/cabinet.service';
import { environment } from 'src/environments/environment';
import { Utilisateur } from 'src/app/utils/interfaces/new/utilisateur.interface';
import { AuthService } from '../auth/auth.service';
import { CpxHandleServiceAbstract } from '../cpx-handle/cpx-handle-service-abstract';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  public logoutInProgress = false;
  public redirectingOn401 = false;

  private isMock = environment.mock;

  private _wait = (ms) => new Promise(res => setTimeout(res, ms));

  constructor(
    private cabinetService: CabinetService,
    private authService: AuthService,
    private cpxHandleSrv: CpxHandleServiceAbstract,
  ) { }

  public findAuth(): (LocalAuth[]) {
    let auths = (JSON.parse(localStorage.getItem('listAuth')) as LocalAuth[]);
    if (!auths) {
      auths = [];
    }
    return auths;
  }

  public findOneAuth(localAuth: LocalAuth): LocalAuth | void {
    const auths = this.findAuth();
    let auth;
    if (!localAuth.profil) {
      auth = auths.find((authInList) => {
        return authInList.utilisateur.email === localAuth.utilisateur.email ? true : false;
      });
    } else {
      auth = auths.find((authInList) => {
        return authInList.profil?.id === localAuth.profil.id ? true : false;
      });
    }
    return auth;
  }

  public saveAuth(localAuth: LocalAuth): void {
    let listProfil = (JSON.parse(localStorage.getItem('listAuth')) as LocalAuth[]);
    if (!listProfil) {
      listProfil = [];
    }
    const auth = this.findOneAuth(localAuth);
    if (!auth) {
      listProfil.push(localAuth);
      localStorage.setItem('listAuth', JSON.stringify(listProfil));
    } else if (auth && auth.utilisateur && localAuth && localAuth.utilisateur && auth.utilisateur.email !== localAuth.utilisateur.email) {
      // If the user email is different, we update the user in the auth
      auth.utilisateur = localAuth.utilisateur;
      const authIndex = listProfil.findIndex((authInList) => authInList.utilisateur.id === auth.utilisateur.id);
      if (authIndex === -1) {
        listProfil.push(localAuth);
      } else {
        listProfil[authIndex] = localAuth;
      }
      localStorage.setItem('listAuth', JSON.stringify(listProfil));
    }
    return;
  }

  public checkSavedProfil(profil: Profil, listProfil: Profil[]): boolean {
    const profilFound = listProfil.find((profilInList) => profilInList.id === profil.id ? true : false);
    if (profilFound) {
      return true;
    } else {
      return false;
    }
  }

  public removeCurrentProfil(): void {
    localStorage.removeItem('currentProfil');
  }

  public saveCurrentProfil(profil: Profil): void {
    const profilString = JSON.stringify(profil);
    localStorage.setItem('currentProfil', profilString);
  }

  public getCurrentProfil(): Profil {
    const profil = JSON.parse(localStorage.getItem('currentProfil')) as Profil;

    if (profil && !profil.utilisateur) {
      const user = this.getCurrentUser();
      if (user) {
        profil.utilisateur = user;
        localStorage.setItem('currentProfil', JSON.stringify(profil));
      }
    }

    return profil;
  }

  public removeCurrentDelegataireProfil(): void {
    localStorage.removeItem('currentDelegataireProfil');
  }

  public saveCurrentDelegataireProfil(profil: Profil): void {
    const profilString = JSON.stringify(profil);
    localStorage.setItem('currentDelegataireProfil', profilString);
  }

  public getCurrentDelegataireProfil(): Profil | undefined {
    const storage = localStorage.getItem('currentDelegataireProfil');
    if (storage) {
      return (JSON.parse(storage) as Profil);
    }
    return;
  }

  public removeCurrentDelegataireUser(): void {
    localStorage.removeItem('currentDelegataireUser');
  }

  public saveCurrentDelegataireUser(user: Utilisateur): void {
    const userString = JSON.stringify(user);
    localStorage.setItem('currentDelegataireUser', userString);
  }

  public getCurrentDelegataireUser(): Utilisateur | undefined {
    const storage = localStorage.getItem('currentDelegataireUser');
    if (storage) {
      return (JSON.parse(storage) as Utilisateur);
    }
    return;
  }

  public removeCurrentUser(): void {
    localStorage.removeItem('currentUser');
  }

  public saveCurrentUser(user: Utilisateur): void {
    const userString = JSON.stringify(user);
    localStorage.setItem('currentUser', userString);
  }

  public getCurrentUser(): Utilisateur {
    return (JSON.parse(localStorage.getItem('currentUser')) as Utilisateur);
  }

  public removeAuthTechInfo(): void {
    localStorage.removeItem('authId');
    localStorage.removeItem('authType');
  }

  public saveAuthTechInfo(authId: string, authType: string): void {
    if(authId) {
      localStorage.setItem('authId', authId);
    }
    if(authType) {
      localStorage.setItem('authType', authType);
    }
  }

  public getAuthTechInfo(): {authId: string, authType: string} {
    return {
      authId: localStorage.getItem('authId'),
      authType: localStorage.getItem('authType'),
    };
  }

  public async getAccountByProfil(profil: Profil): Promise<Account> {
    await this._wait(500);
    let account: Account;
    switch (profil.idUtilisateur) {
      case '1':
        account = {
          login: 'admin@hypermed.fr',
          name: 'admin'
        };
        break;
      case '2':
        account = {
          login: 'user@hypermed.fr',
          name: 'Joël'
        };
        break;
      case '3':
        account = {
          login: 'user2@hypermed.fr',
          name: 'Dr.Hypermed'
        };
        break;
      case '4':
        account = {
          login: 'secretaire@hypermed.fr',
          name: 'Mme Rocher',
        };
        break;
    }
    if (account) {
      account.cabinet = (await this.cabinetService.find()).objects.find((cabinet) => cabinet.id === profil.idCabinet);
    }
    return account;
  }

  public getProfil(idUtilisateur: string, idCabinet: string): any {
    const profil = {
      id: `${idUtilisateur}${idCabinet}`,
      idUtilisateur,
      idCabinet,
    };
    return profil;
  }

  public async renewPassword(email: string, oldPassword: string, newPassword: string): Promise<boolean> {
    return await this.authService.renewPassword(email, oldPassword, newPassword);
  }

  public async forgotPassword(email: string): Promise<boolean> {
    return await this.authService.forgotPassword(email);
  }

  public resetSessionFlags(): void {
    this.logoutInProgress = false;
    this.redirectingOn401 = false;
  }

  public clearLocalSession(): void {
    this.removeAuthTechInfo();
    this.removeCurrentProfil();
    this.removeCurrentUser();
    this.removeCurrentDelegataireProfil();
    this.removeCurrentDelegataireUser();
    localStorage.setItem('openPatients', '[]');
  }
}
